import React, { useState, useRef } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Upload, Link2, ClipboardPaste, Loader2, Users, X, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";

/** Parse text with tab or comma separator.
 *  Columns: name, email, password, [class_year]. Header row optional.
 */
function parseText(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];
  const sep = lines[0].includes("\t") ? "\t" : ",";
  const rows = lines.map((l) => l.split(sep).map((c) => c.trim().replace(/^"|"$/g, "")));
  const first = rows[0].map((c) => c.toLowerCase());
  const hasHeader = first.some((c) => ["name", "email", "password", "full name", "class", "class_year", "year"].includes(c));
  const data = hasHeader ? rows.slice(1) : rows;
  return data
    .filter((r) => r[0] && r[1] && r[2])
    .map((r) => {
      const row = { name: r[0], email: r[1], password: r[2] };
      const yr = (r[3] || "").trim();
      const n = Number(yr);
      if (yr && Number.isInteger(n) && n >= 1900 && n <= 2100) row.class_year = n;
      return row;
    });
}

function parseSheet(arrayBuf) {
  const wb = XLSX.read(arrayBuf, { type: "array" });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
  if (!rows.length) return [];
  const first = (rows[0] || []).map((c) => String(c || "").toLowerCase());
  const hasHeader = first.some((c) => ["name", "email", "password", "class", "class_year", "year"].includes(c));
  const data = hasHeader ? rows.slice(1) : rows;
  return data
    .filter((r) => r && r[0] && r[1] && r[2])
    .map((r) => {
      const row = {
        name: String(r[0]).trim(),
        email: String(r[1]).trim(),
        password: String(r[2]).trim(),
      };
      const yr = String(r[3] || "").trim();
      const n = Number(yr);
      if (yr && Number.isInteger(n) && n >= 1900 && n <= 2100) row.class_year = n;
      return row;
    });
}

function sheetsUrlToCsv(url) {
  const m = url.match(/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  if (!m) return null;
  const id = m[1];
  const gidMatch = url.match(/gid=(\d+)/);
  const gid = gidMatch ? gidMatch[1] : "0";
  return `https://docs.google.com/spreadsheets/d/${id}/export?format=csv&gid=${gid}`;
}

export default function BulkUsersImport({ onImported }) {
  const [mode, setMode] = useState("paste");
  const [text, setText] = useState("");
  const [sheetsUrl, setSheetsUrl] = useState("");
  const [preview, setPreview] = useState([]);
  const [limit, setLimit] = useState("");
  const [asAdmin, setAsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [errors, setErrors] = useState([]);
  const fileRef = useRef(null);

  const clear = () => {
    setPreview([]); setText(""); setSheetsUrl(""); setLimit(""); setErrors([]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handlePaste = () => {
    if (!text.trim()) return toast.error("Paste some rows first");
    const rows = parseText(text);
    if (!rows.length) return toast.error("Couldn't detect rows. Format: name,email,password");
    setPreview(rows); setErrors([]);
    toast.success(`Parsed ${rows.length} rows`);
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLoading(true);
    try {
      const rows = /\.(csv|tsv|txt)$/i.test(f.name)
        ? parseText(await f.text())
        : parseSheet(await f.arrayBuffer());
      if (!rows.length) throw new Error("No rows found");
      setPreview(rows); setErrors([]);
      toast.success(`Parsed ${rows.length} rows from ${f.name}`);
    } catch (err) {
      toast.error(err?.message || "Couldn't parse file");
    } finally {
      setLoading(false);
    }
  };

  const handleSheets = async () => {
    if (!sheetsUrl.trim()) return toast.error("Paste a Google Sheets URL");
    const csvUrl = sheetsUrlToCsv(sheetsUrl);
    if (!csvUrl) return toast.error("Not a valid Google Sheets URL");
    setLoading(true);
    try {
      const resp = await fetch(csvUrl);
      if (!resp.ok) throw new Error("Fetch failed — make sure the sheet is public ('Anyone with the link')");
      const rows = parseText(await resp.text());
      if (!rows.length) throw new Error("No rows detected in the sheet");
      setPreview(rows); setErrors([]);
      toast.success(`Parsed ${rows.length} rows`);
    } catch (err) {
      toast.error(err?.message || "Fetch failed");
    } finally {
      setLoading(false);
    }
  };

  const capped = limit ? preview.slice(0, parseInt(limit)) : preview;

  const doImport = async () => {
    if (!capped.length) return;
    setImporting(true);
    try {
      const { data } = await api.post("/admin/users/bulk", {
        users: capped.map((u) => ({ ...u, is_admin: asAdmin })),
      });
      toast.success(`Created ${data.created} users${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      setErrors(data.errors || []);
      onImported?.(data.users);
      if (!data.skipped) clear();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const modes = [
    { id: "paste", label: "Paste", icon: ClipboardPaste },
    { id: "file", label: "Upload file", icon: Upload },
    { id: "sheets", label: "Google Sheets", icon: Link2 },
  ];

  return (
    <div className="brutal-card p-6 bg-brand-mint/60 mb-6" data-testid="bulk-users-import">
      <h3 className="font-display text-xl font-bold text-brand-navy mb-1 flex items-center gap-2">
        <Users className="w-5 h-5" strokeWidth={3} /> Bulk import users
      </h3>
      <p className="text-brand-navy/70 text-sm mb-4">
        Columns in order: <b>name</b>, <b>email</b>, <b>password</b>, <b>class year</b> (optional). Header row optional (auto-detected).
      </p>

      <div className="flex gap-2 mb-4 flex-wrap">
        {modes.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => { setMode(m.id); clear(); }}
            data-testid={`bulk-users-mode-${m.id}`}
            className={`brutal-badge px-4 py-2 ${mode === m.id ? "bg-brand-yellow" : "bg-white hover:bg-brand-cream"}`}
          >
            <m.icon className="w-3.5 h-3.5" strokeWidth={3} /> {m.label}
          </button>
        ))}
      </div>

      {mode === "paste" && (
        <div className="space-y-3">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="bulk-users-paste-textarea"
            placeholder={`Alice\talice@example.com\tPass123!\nBob\tbob@example.com\tSecret456`}
            className="brutal-input min-h-[140px] bg-white font-mono text-sm"
          />
          <div className="flex justify-end">
            <button type="button" onClick={handlePaste} data-testid="bulk-users-paste-parse" className="brutal-btn-white text-sm py-2">Parse</button>
          </div>
        </div>
      )}

      {mode === "file" && (
        <label className="brutal-card-sm bg-white p-6 flex flex-col items-center gap-2 cursor-pointer hover:bg-brand-cream" data-testid="bulk-users-file-drop">
          <input ref={fileRef} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" onChange={handleFile} className="hidden" />
          <Upload className="w-8 h-8 text-brand-navy" strokeWidth={2.5} />
          <div className="font-bold text-brand-navy">Click to choose CSV / TSV / Excel file</div>
          <div className="text-xs text-brand-navy/60">max 500 users per import</div>
        </label>
      )}

      {mode === "sheets" && (
        <div className="space-y-3">
          <input value={sheetsUrl} onChange={(e) => setSheetsUrl(e.target.value)} data-testid="bulk-users-sheets-url"
            placeholder="https://docs.google.com/spreadsheets/d/…/edit#gid=0" className="brutal-input bg-white" />
          <p className="text-xs text-brand-navy/70">Set the sheet's share access to <b>"Anyone with the link — Viewer"</b>.</p>
          <div className="flex justify-end">
            <button type="button" onClick={handleSheets} disabled={loading} data-testid="bulk-users-sheets-fetch" className="brutal-btn-white text-sm py-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" strokeWidth={3} />} Fetch
            </button>
          </div>
        </div>
      )}

      {loading && <div className="mt-4 flex items-center gap-2 text-brand-navy/70"><Loader2 className="w-4 h-4 animate-spin" /> Parsing…</div>}

      {preview.length > 0 && (
        <div className="mt-6" data-testid="bulk-users-preview">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="font-display font-bold text-brand-navy">
              Preview — <span className="text-brand-pink">{capped.length}</span> of {preview.length} rows
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs font-bold text-brand-navy uppercase">Limit</label>
              <input type="number" min={1} max={preview.length} value={limit} onChange={(e) => setLimit(e.target.value)}
                data-testid="bulk-users-limit" placeholder={String(preview.length)} className="brutal-input w-24 bg-white py-2" />
              <label className="flex items-center gap-2 brutal-badge bg-white cursor-pointer">
                <input type="checkbox" checked={asAdmin} onChange={(e) => setAsAdmin(e.target.checked)} data-testid="bulk-users-as-admin" />
                <ShieldCheck className="w-3.5 h-3.5" strokeWidth={3} /> Make admins
              </label>
              <button onClick={clear} type="button" className="brutal-btn-white text-xs py-2"><X className="w-3.5 h-3.5" strokeWidth={3} /></button>
            </div>
          </div>
          <div className="brutal-card-sm bg-white overflow-hidden">
            <div className="max-h-64 overflow-y-auto divide-y-2 divide-brand-ink/10">
              {capped.slice(0, 200).map((r, i) => (
                <div key={`${r.email}-${i}`} className="grid grid-cols-1 md:grid-cols-3 gap-2 p-2 text-sm">
                  <div className="truncate"><span className="text-brand-navy/50 text-xs mr-1">{i + 1}.</span><span className="font-bold text-brand-navy">{r.name}</span></div>
                  <div className="truncate text-brand-navy/80">{r.email}</div>
                  <div className="truncate text-brand-navy/50 italic">{"•".repeat(Math.min(r.password.length, 8))}</div>
                </div>
              ))}
              {capped.length > 200 && (
                <div className="p-2 text-xs text-brand-navy/60 text-center">
                  … {capped.length - 200} more rows (not shown, but will be imported)
                </div>
              )}
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={doImport} disabled={importing} data-testid="bulk-users-import-btn" className="brutal-btn-pink text-sm py-2">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={3} />}
              Create {capped.length} user{capped.length === 1 ? "" : "s"}
            </button>
          </div>
        </div>
      )}

      {errors.length > 0 && (
        <div className="mt-4 brutal-card-sm p-3 bg-brand-pink/20" data-testid="bulk-users-errors">
          <div className="font-bold text-brand-navy mb-2">Skipped rows ({errors.length}):</div>
          <div className="max-h-40 overflow-y-auto text-sm space-y-1">
            {errors.map((e, i) => (
              <div key={`${e.row}-${e.email || i}`} className="text-brand-navy/80">
                Row {e.row}: <span className="font-mono">{e.email || "—"}</span> — {e.reason}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
